import { isCnoLine } from "./deliveryLines";

export const PRINT_GROUPING_MODES = [
    { key: "line", label: "Actuel" },
    { key: "patient", label: "Par patient" },
    { key: "medicament", label: "Par médicament" },
];

function patientPrintLabel(med) {
    return `${med.patientNom || ""} ${med.patientPrenom || ""}`.trim() || "Patient non renseigné";
}

function patientPrintCell(med) {
    const label = patientPrintLabel(med);
    if (!isCnoLine(med)) return label;

    const details = [
        med.patientAdresse ? `<div style="font-size:10px; color:#555; line-height:1.35; margin-top:3px;">${med.patientAdresse}</div>` : "",
        med.patientTelephone ? `<div style="font-size:10px; color:#555; line-height:1.35;">Tél. : ${med.patientTelephone}</div>` : "",
    ].filter(Boolean).join("");
    return `<div style="font-weight:bold;">${label}</div>${details}`;
}

function medicamentPrintLabel(med) {
    let mention = "";
    if (med.destination === "ROBOT") {
        mention = ' <span style="font-weight:normal; color:#555;">(Sachet)</span>';
    } else if (med.destination === "ETIQUETAGE") {
        mention = ' <span style="font-weight:normal; color:#555;">(Etiquette)</span>';
    }
    return `${med.nom}${med.dosage ? " " + med.dosage : ""}${mention}`;
}

function groupByPrintKey(items, keyBuilder, labelBuilder) {
    const groups = [];
    const byKey = new Map();
    items.forEach(item => {
        const key = keyBuilder(item);
        if (!byKey.has(key)) {
            byKey.set(key, { key, label: labelBuilder(item), items: [] });
            groups.push(byKey.get(key));
        }
        byKey.get(key).items.push(item);
    });
    return groups;
}

const PRINT_CELL_BORDER = "border: 1px solid #333; padding: 5px 8px; font-size: 11px;";

function remainingDeliveryStatus(statut) {
    if (statut === "EN_COMMANDE") return { text: "En commande", bg: "#fef3c7", color: "#92400e" };
    if (statut === "EN_RUPTURE") return { text: "Rupture de stock", bg: "#fee2e2", color: "#991b1b" };
    return null;
}

function renderRemainingDeliveryCell(statut) {
    const remaining = remainingDeliveryStatus(statut);
    if (!remaining) return `<td style="${PRINT_CELL_BORDER}"></td>`;
    return `<td style="${PRINT_CELL_BORDER} background:${remaining.bg}; color:${remaining.color}; font-weight:bold; font-size:10px;">${remaining.text}</td>`;
}

function renderPrintRowsByLine(medicaments) {
    return medicaments.map(med => `
        <tr>
            <td style="${PRINT_CELL_BORDER}">${patientPrintCell(med)}</td>
            <td style="${PRINT_CELL_BORDER}">${medicamentPrintLabel(med)}</td>
            <td style="${PRINT_CELL_BORDER} text-align: center;">${med.quantite ?? ""}</td>
            ${renderRemainingDeliveryCell(med.statut)}
            <td style="${PRINT_CELL_BORDER}"></td>
        </tr>
    `).join("");
}

function renderGroupedPrintRows(groups, firstCell, secondCell) {
    return groups.map(group => group.items.map((med, index) => `
        <tr>
            ${index === 0 ? `<td rowspan="${group.items.length}" style="${PRINT_CELL_BORDER} vertical-align:top; font-weight:bold;">${firstCell(group, med)}</td>` : ""}
            <td style="${PRINT_CELL_BORDER}">${secondCell(med)}</td>
            <td style="${PRINT_CELL_BORDER} text-align: center;">${med.quantite ?? ""}</td>
            ${renderRemainingDeliveryCell(med.statut)}
            <td style="${PRINT_CELL_BORDER}"></td>
        </tr>
    `).join("")).join("");
}

function printTableConfiguration(medicaments, groupingMode) {
    const normalizedMode = PRINT_GROUPING_MODES.some(mode => mode.key === groupingMode) ? groupingMode : "line";
    if (normalizedMode === "patient") {
        const groups = groupByPrintKey(
            medicaments,
            med => patientPrintLabel(med).toLocaleLowerCase("fr-FR"),
            patientPrintCell
        );
        return {
            rows: renderGroupedPrintRows(groups, group => group.label, medicamentPrintLabel),
            headers: { first: "Résidents", second: "Produits", firstWidth: "20%", secondWidth: "40%" },
        };
    }
    if (normalizedMode === "medicament") {
        const groups = groupByPrintKey(
            medicaments,
            med => `${med.nom || ""}|${med.dosage || ""}|${med.destination || ""}`.toLocaleLowerCase("fr-FR"),
            medicamentPrintLabel
        );
        return {
            rows: renderGroupedPrintRows(groups, group => group.label, patientPrintCell),
            headers: { first: "Produits", second: "Résidents", firstWidth: "40%", secondWidth: "20%" },
        };
    }
    return {
        rows: renderPrintRowsByLine(medicaments),
        headers: { first: "Résidents", second: "Produits", firstWidth: "20%", secondWidth: "40%" },
    };
}

/* ─── HTML du bon d'impression ─────────────────────────────────── */
export function generatePrintHTML(ehpad, dateLivraison, pharmacy, isStup = false, livreurName = "", groupingMode = "line") {
    const date = dateLivraison
        ? new Date(dateLivraison).toLocaleString("fr-FR")
        : new Date().toLocaleString("fr-FR");
    const totalUnites = ehpad.medicaments.reduce((acc, m) => acc + (m.quantite || 0), 0);
    const totalLignes = ehpad.medicaments.length;
    const pharmacyName = (pharmacy?.name ?? pharmacy?.nom ?? "").trim();

    const thStyle    = `${PRINT_CELL_BORDER} background: #f0f0f0; font-weight: bold;`;
    const stupAccent = isStup ? "#6d28d9" : "#333";

    const printableMedicaments = ehpad.medicaments.filter(med => med.nom && med.nom.trim() !== "");
    const { rows, headers } = printTableConfiguration(printableMedicaments, groupingMode);

    const livreurLine = livreurName
        ? `<div>Livré par : <strong>${livreurName}</strong></div>`
        : `<div>Livré par &nbsp;<span style="display:inline-block; width:54%; border-bottom:1px solid #333;"></span></div>`;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Bon de livraison${isStup ? " — STUP" : ""} — ${ehpad.ehpadNom}</title>
    <style>
        @page {
            margin: 1.5cm 1.5cm 2cm;
            @bottom-center {
                content: "— " counter(page) " —";
                font-family: Arial, sans-serif;
                font-size: 9px;
                color: #666;
            }
        }
        @media screen { body { margin: 1.5cm 1.5cm 2cm; } }
        @media print  { body { margin: 0; } }
        body   { font-family: Arial, sans-serif; font-size: 12px; color: #000; padding: 0; }
        table  { width: 100%; border-collapse: collapse; }
        tr     { break-inside: avoid; page-break-inside: avoid; }
        thead  { display: table-header-group; }
        .no-break { break-inside: avoid; page-break-inside: avoid; }
    </style>
</head>
<body>

    ${isStup ? `<div style="background:#ede9fe; border:2px solid #6d28d9; border-radius:4px; padding:6px 14px; margin-bottom:12px; text-align:center; font-size:12px; font-weight:bold; color:#6d28d9; letter-spacing:0.08em; text-transform:uppercase;">
        ⚠ Stupéfiants — Document à conserver
    </div>` : ""}

    <div style="display:flex; justify-content:space-between; align-items:stretch; margin-bottom:16px; gap:12px;">
        <div style="border:1.5px solid #333; border-radius:4px; padding:10px 14px; min-width:200px; line-height:1.8;">
            <div style="font-weight:bold; font-size:13px; margin-bottom:4px; text-transform:uppercase; letter-spacing:0.04em;">
                ${pharmacyName}
            </div>
            ${pharmacy?.address ? `<div style="font-size:11px;">${pharmacy.address}</div>` : ""}
            ${pharmacy?.phone   ? `<div style="font-size:11px;">Tél. : ${pharmacy.phone}</div>` : ""}
        </div>
        <div style="flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:6px;">
            <div style="font-size:14px; font-weight:bold; text-transform:uppercase; letter-spacing:0.06em; color:${stupAccent};">
                Bon de livraison${isStup ? " — Stupéfiants" : ""}
            </div>
            <div style="font-size:10px; color:#555;">Édité le ${date}</div>
            <div style="font-size:11px; background:#f5f5f5; border:1px solid #ddd; padding:3px 10px; border-radius:4px;">
                ${totalLignes} ligne${totalLignes > 1 ? "s" : ""}
                &nbsp;·&nbsp;
                <strong>${totalUnites} unité${totalUnites > 1 ? "s" : ""}</strong> à livrer
            </div>
        </div>
        <div style="border:1.5px solid #333; border-radius:4px; padding:10px 14px; min-width:200px; text-align:right; line-height:1.8;">
            <div style="font-weight:bold; font-size:13px; margin-bottom:4px; text-transform:uppercase; letter-spacing:0.04em;">
                ${ehpad.ehpadNom}
            </div>
            ${ehpad.ehpadAddress ? `<div style="font-size:11px;">${ehpad.ehpadAddress}</div>` : ""}
            ${ehpad.ehpadPhone   ? `<div style="font-size:11px;">Tél. : ${ehpad.ehpadPhone}</div>` : ""}
        </div>
    </div>

    <table style="margin-bottom:28px;">
        <thead>
            <tr>
                <th style="${thStyle} width:${headers.firstWidth};">${headers.first}</th>
                <th style="${thStyle} width:${headers.secondWidth};">${headers.second}</th>
                <th style="${thStyle} width:10%; text-align:center;">Qté livrée</th>
                <th style="${thStyle} width:15%;">Reste à livrer</th>
                <th style="${thStyle} width:15%;">Commentaires</th>
            </tr>
        </thead>
        <tbody>
            ${rows}
        </tbody>
    </table>

    <table class="no-break">
        <tr>
            <td style="border:1px solid #333; padding:14px; width:47%; vertical-align:top; font-size:12px; line-height:2.4;">
                ${livreurLine}
                <div>Date et heure &nbsp;<span style="display:inline-block; width:52%; border-bottom:1px solid #333;"></span></div>
                <div>Signature &nbsp;<span style="display:inline-block; width:62%; border-bottom:1px solid #333;"></span></div>
            </td>
            <td style="width:6%; border:none;"></td>
            <td style="border:1px solid #333; padding:14px; width:47%; vertical-align:top; font-size:12px; line-height:2.4;">
                <div>Réceptionné par &nbsp;<span style="display:inline-block; width:46%; border-bottom:1px solid #333;"></span></div>
                <div>Date et heure &nbsp;<span style="display:inline-block; width:52%; border-bottom:1px solid #333;"></span></div>
                <div>Signature &nbsp;<span style="display:inline-block; width:62%; border-bottom:1px solid #333;"></span></div>
            </td>
        </tr>
    </table>

</body>
</html>`;
}

export function openPrintWindow(ehpad, dateLivraison, pharmacy, isStup = false, livreurName = "", groupingMode = "line") {
    const html = generatePrintHTML(ehpad, dateLivraison, pharmacy, isStup, livreurName, groupingMode);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = globalThis.open(url, "_blank", "width=900,height=700");
    if (!win) {
        URL.revokeObjectURL(url);
        return;
    }
    win.addEventListener("load", () => {
        URL.revokeObjectURL(url);
        win.focus();
        win.print();
    }, { once: true });
}
